// Iter 82.7 / Phase E2.3 — Attendance Trends Report.
// Time-series view: daily attendance %, weekly aggregates, anomaly counts.
import { useCallback, useEffect, useState } from "react";
import { DownloadSimple, FileXls, ChartLineUp } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const WINDOW_OPTIONS = [
    { v: 7, l: "Last 7 days" },
    { v: 14, l: "Last 14 days" },
    { v: 28, l: "Last 28 days" },
    { v: 90, l: "Last 90 days" },
    { v: 180, l: "Last 180 days" },
];

function pctBand(pct) {
    if (pct >= 95) return "bg-emerald-500";
    if (pct >= 85) return "bg-amber-500";
    return "bg-rose-500";
}

export default function AttendanceTrendsReport() {
    const [days, setDays] = useState(28);
    const [data, setData] = useState({ daily_series: [], weekly_series: [] });
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/attendance/report/trends", {
                params: { days_window: days },
            });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load trends"));
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { reload(); }, [reload]);

    const download = async (ext) => {
        try {
            const { data: blob } = await api.get(`/attendance/report/trends/export.${ext}`, {
                params: { days_window: days }, responseType: "blob",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `attendance-trends-${new Date().toISOString().slice(0, 10)}.${ext}`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err, `${ext.toUpperCase()} export failed`)); }
    };

    const dailyAvg = data.daily_series && data.daily_series.length > 0
        ? data.daily_series.reduce((sum, b) => sum + b.attendance_pct, 0) / data.daily_series.length
        : 0;
    const totalAnomalies = (data.daily_series || []).reduce((s, b) => s + (b.anomaly_count || 0), 0);
    const totalLate = (data.daily_series || []).reduce((s, b) => s + (b.late_count || 0), 0);
    const totalAbsent = (data.daily_series || []).reduce((s, b) => s + (b.absent_count || 0), 0);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="att-trends-page">
            <PageHeader
                eyebrow="Attendance Reports"
                title="Attendance Trends"
                description={`${data.from_date} → ${data.to_date} · ${data.days_window || days} day window · ${data.scope || ""}`}
                actions={(
                    <div className="flex gap-2 items-center">
                        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                                className="text-xs border border-slate-300 rounded-md px-2 py-1.5"
                                data-testid="att-trends-window">
                            {WINDOW_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="att-trends-csv-btn">
                            <DownloadSimple size={14} className="mr-1" /> CSV
                        </Button>
                        <Button size="sm" onClick={() => download("xlsx")} data-testid="att-trends-xlsx-btn">
                            <FileXls size={14} className="mr-1" /> XLSX
                        </Button>
                    </div>
                )}
            />

            <section className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="att-trends-kpis">
                <Kpi label="Avg Attendance %" value={dailyAvg.toFixed(1)} suffix="%" tone="sky" testid="trends-kpi-avg" />
                <Kpi label="Total Absences" value={totalAbsent} tone="rose" testid="trends-kpi-absent" />
                <Kpi label="Total Late" value={totalLate} tone="amber" testid="trends-kpi-late" />
                <Kpi label="Total Anomalies" value={totalAnomalies} tone="orange" testid="trends-kpi-anomalies" />
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="att-trends-daily-chart">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <ChartLineUp size={14} /> Daily Attendance % · {days} days
                    </h3>
                </div>
                {loading ? (
                    <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
                ) : (
                    <div className="flex items-end gap-1 h-48" data-testid="trends-bars">
                        {data.daily_series.map((b) => (
                            <div key={b.date} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`trend-day-${b.date}`}>
                                <div className="text-[9px] text-slate-500 font-mono opacity-0 group-hover:opacity-100 transition">{b.attendance_pct.toFixed(0)}%</div>
                                <div className="w-full bg-slate-100 rounded-t flex items-end" style={{ height: "100%" }}>
                                    <div className={`w-full rounded-t ${pctBand(b.attendance_pct)} transition-all`}
                                         style={{ height: `${(b.attendance_pct / 100) * 100}%` }}
                                         title={`${b.date}: ${b.attendance_pct.toFixed(1)}% · ${b.late_count} late · ${b.absent_count} absent · ${b.anomaly_count} anomalies`} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="mt-3 flex gap-4 text-[10px] text-slate-500 justify-center">
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded-sm" />≥95%</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500 rounded-sm" />85-95%</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 bg-rose-500 rounded-sm" />&lt;85%</span>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="att-trends-weekly-table">
                <div className="px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800">Weekly Aggregates</h3>
                </div>
                {data.weekly_series && data.weekly_series.length > 0 ? (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Week</th>
                                <th className="text-right px-3 py-2 font-medium">Present</th>
                                <th className="text-right px-3 py-2 font-medium">Absent</th>
                                <th className="text-right px-3 py-2 font-medium">Half Day</th>
                                <th className="text-right px-3 py-2 font-medium">On Leave</th>
                                <th className="text-right px-3 py-2 font-medium">Late</th>
                                <th className="text-right px-3 py-2 font-medium">Early Dep</th>
                                <th className="text-right px-3 py-2 font-medium">Regularized</th>
                                <th className="text-right px-3 py-2 font-medium">Anomalies</th>
                                <th className="text-right px-3 py-2 font-medium">Attendance %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.weekly_series.map((w) => (
                                <tr key={w.week} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`trends-week-${w.week}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{w.week}</td>
                                    <td className="px-3 py-2 text-right text-emerald-700">{w.present}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{w.absent}</td>
                                    <td className="px-3 py-2 text-right text-amber-700">{w.half_day}</td>
                                    <td className="px-3 py-2 text-right text-sky-700">{w.on_leave}</td>
                                    <td className="px-3 py-2 text-right text-orange-700">{w.late}</td>
                                    <td className="px-3 py-2 text-right text-orange-700">{w.early_departure}</td>
                                    <td className="px-3 py-2 text-right text-violet-700">{w.regularized}</td>
                                    <td className="px-3 py-2 text-right">{w.anomalies}</td>
                                    <td className="px-3 py-2 text-right">
                                        <span className={`font-bold ${w.attendance_pct >= 95 ? "text-emerald-700" : w.attendance_pct >= 85 ? "text-amber-700" : "text-rose-700"}`}>
                                            {w.attendance_pct.toFixed(1)}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-center py-12 text-sm text-slate-400">No weekly aggregates yet.</div>
                )}
            </section>
        </div>
    );
}

function Kpi({ label, value, suffix = "", tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
    };
    return (
        <div className={`rounded-xl border p-4 ${map[tone]}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-3xl font-bold mt-2">{value}{suffix && <span className="text-sm font-normal opacity-70 ml-1">{suffix}</span>}</div>
        </div>
    );
}
